import { Component, OnInit } from '@angular/core';
import { GridBaseComponent } from '../../../base/shared/grid-base/grid-base.component';
import { ClientService } from '../../../base/services/client/client.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ParkingAdministrationService } from './parking-administration.service';
import { DataManager } from '@syncfusion/ej2-data';
import { AvpCustomAdaptor } from '../../../base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from '../../../base/spinner/spinner.service';
import { AbstractControl, FormBuilder, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { CustomValidatorsFactory } from '../../../base/shared/validators/custom-validators-factory';
import {
  AutomaticParkingScheduleDTO,
  CreateAutomaticParkingScheduleDTO,
  UpdateAutomaticParkingScheduleDTO,
  WeekDayFlags,
} from 'src/app/base/models/models.swagger.generated';
import { AuthService } from '../../../base/services/auth.service';
import { DateHelper } from '../../../base/shared/date-helper';
import { BitHelper } from '../../../base/shared/bit-helper';
import { WeekDayFlagsHelper } from 'src/app/base/shared/utils/week-day-flags.helper';
import { DateTime } from 'luxon';
import { ParkingAdministrationEditFormComponent } from './parking-administration-edit-form/parking-administration-edit-form.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { GridModule } from '@syncfusion/ej2-angular-grids';
import { NgIf } from '@angular/common';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { AutomaticParkingScheduleEditForm } from './parking-administration-edit-form/parking-administration-edit-form.type';

@Component({
    selector: 'app-parking-administration',
    templateUrl: './parking-administration.component.html',
    styleUrls: ['./parking-administration.component.scss'],
    standalone: true,
    imports: [TitleBarComponent, NgIf, GridModule, ToolbarModule, ParkingAdministrationEditFormComponent, TranslateModule]
})
export class ParkingAdministrationComponent extends GridBaseComponent implements OnInit {
  public get hasLinkedParker(): boolean {
    return this.authService.getUserInfo()?.linkedParkers?.length > 0;
  }

  constructor(clientService: ClientService,
              translate: TranslateService,
              private authService: AuthService,
              private fb: FormBuilder,
              private parkingAdministrationService: ParkingAdministrationService,
              private spinnerService: SpinnerService) {
    super(clientService, translate, parkingAdministrationService);
  }

  public ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.parkingAdministrationService, this.spinnerService),
    });
    this.isActiveColumnName = 'active';
    this.editDialogTitle = 'i18n.domain.parking-administration.edit-dialog-title';
    this.addDialogTitle = 'i18n.domain.parking-administration.add-dialog-title';
    this.addIndexFindColumnName = 'name';
  }

  public createOverlappingSchedulesValidation(schedules: AutomaticParkingScheduleDTO[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if(!schedules?.length) {
        return null;
      }
      const idToExclude = control.get('id').value;
      const tagId = control.get('controlPlateId').value;
      const parkingDuration = control.get('duration').value;
      const startTimeAsJsDate = control.get('startTime').value;
      const weekDayFlags = WeekDayFlagsHelper.daysToFlag(control.get('weekdays').value);
      if (!tagId || !parkingDuration || !startTimeAsJsDate || !weekDayFlags) {
        return null;
      }
      const starTimeAsDateTime = DateTime.fromJSDate(startTimeAsJsDate);
      const startTimeMillis = starTimeAsDateTime.diff(starTimeAsDateTime.startOf('day')).toMillis();
      const schedule = schedules.find((s) => {
        if (idToExclude && s.id === idToExclude || s.tag?.id !== tagId) {
          return false;
        }
        // eslint-disable-next-line no-bitwise
        if (!(weekDayFlags & s.onWeekDays)){
          return false;
        }
        const schedStartTimeMillis = s.parkingStartTime.toMillis();
        const schedEndTimeMillis = schedStartTimeMillis + s.parkingDurationInMinutes * 60000;
        const endTimeMillis = startTimeMillis + parkingDuration * 60000;
        if (schedStartTimeMillis > endTimeMillis || schedEndTimeMillis < startTimeMillis) {
          return false;
        }
        return true;
      });
      if (schedule) {
        return {overlappingSchedule: schedule};
      }
      return null;
    };
  }

  protected getEditDialogTitleParams(rowData: any): any {
    return {name: rowData.name};
  }

  protected initializeEditForm(data: AutomaticParkingScheduleDTO): void {
    this.gridEditForm = this.fb.group<AutomaticParkingScheduleEditForm>({
      id: this.fb.control(data.id),
      isActive: this.fb.control(!!data.id ? data.isActive : true, [Validators.required]),
      name: this.fb.control(data.name, [Validators.required]),
      clientId: this.fb.control(data.client?.id, [Validators.required]),
      subscriptionId: this.fb.control(data.subscriptionAssignment?.id, [Validators.required]),
      zoneId: this.fb.control(data.zone?.id, [Validators.required]),
      parkingSpaceNumber: this.fb.control(data.parkingSpaceNumber),
      controlPlateId: this.fb.control(data.tag?.id, [Validators.required]),
      weekdays: this.fb.group({
        allowedMonday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 0)),
        allowedTuesday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 1)),
        allowedWednesday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 2)),
        allowedThursday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 3)),
        allowedFriday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 4)),
        allowedSaturday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 5)),
        allowedSunday: this.fb.control(BitHelper.isBitSet(data.onWeekDays, 6)),
      }, {validators: [CustomValidatorsFactory.atLeastOneFromFormGroupRequired()]}),
      startTime: this.fb.control(data.parkingStartTime ? DateHelper.setDateTimeTo1970FromDuration(data.parkingStartTime).toJSDate() : null, [Validators.required]),
      duration: this.fb.control(data.parkingDurationInMinutes, [Validators.required]),
    }, {validators: [this.createOverlappingSchedulesValidation(this.parkingAdministrationService.automaticParkingSchedules)]});
  }


  protected override getDataToSave(): any {
    const data = this.gridEditForm.getRawValue();

    let weekdays = 0;
    if (data.weekdays.allowedMonday) {
      weekdays += WeekDayFlags.Monday;
    }
    if (data.weekdays.allowedTuesday) {
      weekdays += WeekDayFlags.Tuesday;
    }
    if (data.weekdays.allowedWednesday) {
      weekdays += WeekDayFlags.Wednesday;
    }
    if (data.weekdays.allowedThursday) {
      weekdays += WeekDayFlags.Thursday;
    }
    if (data.weekdays.allowedFriday) {
      weekdays += WeekDayFlags.Friday;
    }
    if (data.weekdays.allowedSaturday) {
      weekdays += WeekDayFlags.Saturday;
    }
    if (data.weekdays.allowedSunday) {
      weekdays += WeekDayFlags.Sunday;
    }

    if (data.id) {
      return new UpdateAutomaticParkingScheduleDTO({
        id: data.id,
        name: data.name,
        zoneId: data.zoneId,
        tagId: data.controlPlateId,
        parkingSpaceNumber: data.parkingSpaceNumber,
        parkingStartTime: DateHelper.getTimeOfDayDurationFromJsDate(data.startTime),
        parkingDurationInMinutes: data.duration,
        subscriptionAssignmentId: data.subscriptionId,
        isActive: data.isActive,
        onWeekDays: weekdays,
      });
    }

    return new CreateAutomaticParkingScheduleDTO({
      name: data.name,
      clientId: data.clientId,
      zoneId: data.zoneId,
      subscriptionAssignmentId: data.subscriptionId,
      parkingSpaceNumber: data.parkingSpaceNumber,
      parkingStartTime: DateHelper.getTimeOfDayDurationFromJsDate(data.startTime),
      parkingDurationInMinutes: data.duration,
      tagId: data.controlPlateId,
      onWeekDays: weekdays,
    });
  }
}
